// ============================================
// Imports - Sound Files
// ============================================
import score_0_20_sound_01 from '../assets/sounds/score_0_20_sound_01.mp3';
import score_0_20_sound_03 from '../assets/sounds/score_0_20_sound_03.mp3';
import score_0_20_sound_04 from '../assets/sounds/score_0_20_sound_04.mp3';

import score_20_50_sound_01 from '../assets/sounds/score_20_50_sound_01.mp3';
import score_20_50_sound_02 from '../assets/sounds/score_20_50_sound_02.mp3';

import score_50_70_sound_01 from '../assets/sounds/score_50_70_sound_01.mp3';
import score_70_80_sound_02 from '../assets/sounds/score_70_80_sound_01.mp3';
import score_80_95_sound_01 from '../assets/sounds/score_80_95_sound_01.mp3';
import score_95_100_sound_01 from '../assets/sounds/score_95_100_sound_01.mp3';

// ספירה לפני תחילת קריאה - "שלוש, שתיים, אחת" בקולו של אלי

// ============================================
// Constants
// ============================================
const SOUND_MAP: Readonly<Record<string, readonly string[]>> = {
  '0-20': [score_0_20_sound_01, score_0_20_sound_03, score_0_20_sound_04],
  '20-50': [score_20_50_sound_01, score_20_50_sound_02],
  '50-70': [score_50_70_sound_01],
  '70-80': [score_70_80_sound_02],
  '80-95': [score_80_95_sound_01],
  '95-100': [score_95_100_sound_01],
} as const;

const STORAGE_KEY = 'sound_volume';
const DEFAULT_VOLUME = 0.35;
const MIN_VOLUME = 0.0;
const MAX_VOLUME = 1.0;

// ============================================
// State - Global State
// ============================================
const preloadedSounds: Map<string, HTMLAudioElement> = new Map();
let currentVolume: number = DEFAULT_VOLUME;

// ============================================
// Helper Functions
// ============================================

/**
 * Loads the sound volume from localStorage
 */
const loadStoredVolume = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      const volume = Number.parseFloat(stored);
      if (Number.isFinite(volume) && volume >= MIN_VOLUME && volume <= MAX_VOLUME) {
        return volume;
      }
    }
  } catch (error) {
    console.warn('שגיאה בטעינת עוצמת השמע מ-localStorage:', error);
  }
  return DEFAULT_VOLUME;
};

/**
 * Initializes the sound volume from storage
 */
const initializeVolume = (): void => {
  currentVolume = loadStoredVolume();
};

/**
 * Creates an error handler for sound files
 */
const createAudioErrorHandler = (range: string, index: number) => (e: Event): void => {
  console.warn(`ניסיון טעינה שגיאה: קובץ השמע לא נגיש (חסום או לא קיים). טווח: ${range}, אינדקס: ${index}`, e);
};

/**
 * Validates if a sound source is valid
 */
const isValidSoundSource = (src: unknown): src is string => {
  return typeof src === 'string' && src !== '' && src !== 'undefined';
};

/**
 * Creates an Audio element with basic settings
 */
const createAudioElement = (src: string, errorHandler: (e: Event) => void): HTMLAudioElement => {
  const audio = new Audio(src);
  audio.preload = 'auto';
  audio.volume = currentVolume;
  audio.addEventListener('error', errorHandler);
  return audio;
};

/**
 * Updates the volume of all active audio objects
 */
const updateActiveAudioVolume = (volume: number): void => {
  preloadedSounds.forEach((audio) => {
    if (audio && !audio.paused && !audio.ended) {
      audio.volume = volume;
    }
  });
};

// ============================================
// Sound Selection Logic
// ============================================

/**
 * Returns the sound array matching the given score
 */
const getSoundArrayForScore = (score: number): readonly string[] => {
  if (score >= 95) return SOUND_MAP['95-100'];
  if (score >= 80) return SOUND_MAP['80-95'];
  if (score >= 70) return SOUND_MAP['70-80'];
  if (score >= 50) return SOUND_MAP['50-70'];
  if (score >= 20) return SOUND_MAP['20-50'];
  return SOUND_MAP['0-20'];
};

/**
 * Selects a random sound file from an array
 */
const selectRandomSound = (soundArray: readonly string[]): string => {
  if (soundArray.length === 0) {
    console.warn('מערך שמע ריק - מחזיר ברירת מחדל');
    return SOUND_MAP['0-20']?.[0] || '';
  }
  const randomIndex = Math.floor(Math.random() * soundArray.length);
  return soundArray[randomIndex];
};

/**
 * Returns a random sound file based on score
 */
const getRandomSound = (score: number): string => {
  const soundArray = getSoundArrayForScore(score);
  
  if (!soundArray || soundArray.length === 0) {
    console.warn(`אין קבצי שמע זמינים לטווח הציון ${score}`);
    return SOUND_MAP['0-20']?.[0] || '';
  }

  return selectRandomSound(soundArray);
};

// ============================================
// Volume Management
// ============================================

/**
 * Sets the sound volume (0.0 to 1.0)
 */
const setSoundVolume = (volume: number): void => {
  const clampedVolume = Math.max(MIN_VOLUME, Math.min(MAX_VOLUME, volume));
  currentVolume = clampedVolume;

  try {
    localStorage.setItem(STORAGE_KEY, clampedVolume.toString());
  } catch (error) {
    console.warn('שגיאה בשמירת עוצמת השמע ב-localStorage:', error);
  }

  updateActiveAudioVolume(clampedVolume);
};

/**
 * Returns the current sound volume (0.0 to 1.0)
 */
const getSoundVolume = (): number => {
  return currentVolume;
};

// ============================================
// Audio Preloading
// ============================================

/**
 * Preloads all sound files
 */
const preloadAllSounds = (): void => {
  Object.entries(SOUND_MAP).forEach(([range, soundArray]) => {
    soundArray.forEach((soundSrc, index) => {
      if (!isValidSoundSource(soundSrc)) {
        console.error(`שגיאה: קובץ הסאונד לא מיובא כראוי! טווח: ${range}, אינדקס: ${index}`);
        return;
      }

      // Skip if file is already loaded
      if (preloadedSounds.has(soundSrc)) {
        return;
      }

      const errorHandler = createAudioErrorHandler(range, index);
      const audio = createAudioElement(soundSrc, errorHandler);
      
      // Load metadata
      audio.load();
      
      // Cache the audio element
      preloadedSounds.set(soundSrc, audio);
    });
  });
};

// ============================================
// Audio Retrieval
// ============================================

/**
 * General error handler for audio
 */
const handleAudioError = (e: Event): void => {
  console.warn('ניסיון ניגון שגיאה: קובץ השמע לא נגיש (חסום או לא קיים)', e);
};

/**
 * Returns an Audio object ready to use based on score
 */
const getPreloadedAudio = (score: number): HTMLAudioElement => {
  const soundSrc = getRandomSound(score);

  if (!isValidSoundSource(soundSrc)) {
    console.warn('מקור שמע לא תקין - יצירת אובייקט ריק');
    const emptyAudio = new Audio();
    emptyAudio.volume = currentVolume;
    return emptyAudio;
  }

  // If file is already cached, create a clone
  const cachedAudio = preloadedSounds.get(soundSrc);
  if (cachedAudio) {
    const clonedAudio = new Audio();
    clonedAudio.src = cachedAudio.src;
    clonedAudio.volume = currentVolume;
    clonedAudio.addEventListener('error', handleAudioError, { once: true });
    return clonedAudio;
  }

  // If not cached, create a new audio object
  const newAudio = createAudioElement(soundSrc, handleAudioError);
  return newAudio;
};

// ============================================
// Flow Chimes
// ============================================

/** מעל זה נחשב טוב - אותו סף שבו מופיע הקונפטי במסך הרגיל */
const GOOD_SCORE = 80;

let chimeContext: AudioContext | null = null;

/**
 * צליל קצר בסוף פסוק בזרימה רציפה: "בּינג" עולה כשטוב, "בּונג" יורד כשפחות.
 * נוצר בקוד ולא מקובץ, כדי שיישאר קצר ולא ייגרר כמו מנגינת ניצחון.
 */
const playFlowChime = (score: number): void => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    if (!chimeContext || chimeContext.state === 'closed') {
      chimeContext = new AudioContextClass();
    }
    if (chimeContext.state === 'suspended') {
      chimeContext.resume().catch(() => undefined);
    }

    const isGood = score >= GOOD_SCORE;
    const now = chimeContext.currentTime;
    const duration = isGood ? 0.22 : 0.32;

    const oscillator = chimeContext.createOscillator();
    const gain = chimeContext.createGain();

    oscillator.type = 'sine';
    if (isGood) {
      oscillator.frequency.setValueAtTime(880, now);
      oscillator.frequency.exponentialRampToValueAtTime(1320, now + duration);
    } else {
      oscillator.frequency.setValueAtTime(420, now);
      oscillator.frequency.exponentialRampToValueAtTime(240, now + duration);
    }

    // עלייה מהירה ודעיכה, בלי קליק בהתחלה ובסוף
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(currentVolume, 0.01), now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(gain);
    gain.connect(chimeContext.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  } catch (error) {
    console.warn('שגיאה בניגון צליל הזרימה (לא קריטי):', error);
  }
};
// ============================================
// Initialization
// ============================================
initializeVolume();

// ============================================
// Exports - Public Functions
// ============================================
export { 
  getRandomSound, 
  preloadAllSounds, 
  getPreloadedAudio, 
  setSoundVolume, 
  getSoundVolume,
  playFlowChime,
};
